<template>
  <div class="d-flex fl-col fl-s-b aside-wrapper" data-cy="aside-order-summary">
    <div class="order-summary-wrapper d-flex fl-col fl-s-b scroll-y">
      <div class="scroll-y order-summary d-flex fl-col fl-s-b">
        <div class="upper-part-container d-flex flex-row fl-s-b">
          <div class="title d-flex fl-col fl-j-c">
            <p
              :class="{
                'long-title': isLongTitle,
                'max-content-width': !isLongTitle,
              }"
            >
              {{ $t("asideordersummary.title") }}
            </p>
            <div class="order-reference-wrapper">
              <p class="order-reference" data-cy="order-reference">
                {{ $t("orders.reference") }}: {{ orderReference }}
              </p>
              <div class="clipboard">
                <SvgIcon
                  :iconContent="copyIcon"
                  iconStyle="width: 12px; height: 12px; cursor:pointer;"
                  @click="copyToClipboard(orderReference)"
                />
                <SvgIcon
                  v-if="showCopyCheck"
                  :iconContent="check"
                  iconStyle="width: 12px; height: 12px; cursor:pointer"
                  class="check"
                />
              </div>
            </div>
          </div>
          <div
            v-if="showPrintBill"
            class="print-receipt-btn-container d-flex flex-row fl-s-b flex-align-center"
            @click="printBill()"
            data-cy="print-bill-btn"
          >
            <div class="icon-container">
              <SvgIcon
                :iconContent="printIcon"
                :iconAlt="printAlt"
                :iconStyle="iconStyle"
              />
            </div>
            <p class="max-content-width">
              {{ $t("completeorder.print") }}
            </p>
          </div>
          <div
            class="assign-customer-btn-container d-flex flex-row fl-s-b flex-align-center"
            data-cy="assign-customer-button"
            @click="onClickCustomers"
          >
            <div class="icon-container">
              <SvgIcon
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </div>
            <p class="max-content-width">
              {{ getCustomerFullName }}
            </p>
          </div>
        </div>

        <!-- Waiver section — shown in completed step when booking requires a waiver -->
        <WaiverSection
          v-if="isCompletedBooking && isWaiverAllowed"
          :linkedCount="linkedCount"
          :isLoadingCount="isLoadingCount"
        />

        <div class="pss-list-container d-flex fl-col fl-s-a">
          <!-- style="position:relative;" -->
          <div v-if="groupedPSSList">
            <div
              v-for="pss in groupedPSSList"
              :key="String(pss.uuid)"
              :class="
                pss.bookingStatus === BookingStatus.Canceled
                  ? 'cancelled-pss-container'
                  : ''
              "
            >
              <div class="pss-container d-flex flex-row fl-s-b">
                <div class="ticket-name-container">
                  <p class="ticket-name">
                    {{
                      // In case of a retail with only one option which is the default one we show category name
                      pss.pssType === PSSType[PSSType.Retail]
                        ? pss.optionName || pss.categoryName
                        : pss.categoryName
                    }}
                  </p>
                  <p class="price-name" v-if="showTicketInfo(pss)">
                    {{ pss.priceName }}
                  </p>
                  <p class="timeslot" v-if="showTicketInfo(pss)">
                    {{ formatDateTime(pss.startDateTime) }}
                  </p>
                  <p v-if="pss.seats && pss.seats.length" class="seat">
                    {{
                      pss.seats.length > 1
                        ? `${t("field.seat")}: ${pss.seats.join(", ")}`
                        : `${t("field.seat")}: ${pss.seats[0]}`
                    }}
                  </p>
                  <p
                    class="ticket-discount-info"
                    v-if="
                      (hasGenericDiscountAccountings(pss) ||
                        isPSSConsideredInDiscount1(pss)) &&
                      !isFreeTicket(pss)
                    "
                  >
                    {{ getTicketDiscount1(pss) }}
                  </p>
                  <p
                    class="ticket-discount-info"
                    v-for="line of getTicketAppliedDiscountCodes(pss)"
                    :key="line"
                  >
                    {{ line }}
                  </p>
                  <p
                    class="ticket-discount-info"
                    v-for="line of getTicketAppliedOffers(pss)"
                    :key="line"
                  >
                    {{ line }}
                  </p>
                </div>
                <div class="quantity-container">
                  <p
                    class="quantity"
                    :class="
                      pss.bookingStatus === BookingStatus.Canceled
                        ? 'erased-tickets'
                        : ''
                    "
                  >
                    X{{ pss.quantity }}
                  </p>
                  <Trash2Icon
                    v-if="showRemoveBtn(pss)"
                    class="remove-btn"
                    @click="deleteTickets(pss.pssIds)"
                    data-cy="remove-pss-button"
                  />
                </div>
                <div class="unit-price-container">
                  <p
                    class="unit-price"
                    :class="
                      pss.bookingStatus === BookingStatus.Canceled
                        ? 'erased-tickets'
                        : ''
                    "
                  >
                    {{ (getPSSValue(pss) / 100).toFixed(2) }}
                  </p>
                </div>
                <div
                  class="total-price-container"
                  :class="{ 'with-pt': getPssGroupDiscount(pss, false) }"
                >
                  <p
                    class="total-price"
                    :class="{
                      'erased-tickets':
                        pss.bookingStatus === BookingStatus.Canceled ||
                        getPssGroupDiscount(pss, false),
                    }"
                  >
                    {{ ((getPSSValue(pss) / 100) * pss.quantity).toFixed(2) }}
                  </p>
                  <p class="total-price" v-if="getPssGroupDiscount(pss, false)">
                    {{
                      (
                        (getPSSValue(pss) / 100) * pss.quantity -
                        getPssGroupDiscount(pss, false) / 100
                      ).toFixed(2)
                    }}
                  </p>
                </div>
              </div>
              <div
                v-if="
                  (pss.bookingStatus === BookingStatus.Confirmed ||
                    pss.bookingStatus === BookingStatus.Canceled) &&
                  isEditOrder
                "
                class="tickets-status-container"
              >
                <p>
                  {{ getStatusTranslation(pss) }}
                </p>
              </div>
            </div>
          </div>

          <el-row
            v-if="isInPaymentStep && discountTotal1"
            class="total-container discount-container"
            justify="start"
          >
            <el-col :span="5" class="total-container">
              {{ `${$t("button.discount")}:` }}
            </el-col>
            <el-col :span="5" class="total-container d-flex fl-a-c fl-j-e">
              {{ selectedBooking?.currency }}
            </el-col>
            <el-col :span="11" class="d-flex flex-row fl-a-c fl-j-e"> </el-col>
            <el-col class="total-container text-right" :span="3">
              {{ (discountTotal1 / 100).toFixed(2) }}
            </el-col>
          </el-row>
        </div>
        <!-- Subtotal -->
        <div
          v-if="isInPaymentStep || isCompletedBooking"
          class="w-full d-flex flex-1 fl-s-e fl-a-c"
        >
          <div class="total-container text-left" style="width: 33%">
            {{ `${$t("field.subtotal")}:` }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ selectedBooking?.currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ (Number(total1) - Number(totalTax)).toFixed(2) }}
          </div>
        </div>

        <!-- Tax -->
        <div
          v-if="isInPaymentStep || isCompletedBooking"
          class="w-full d-flex flex-1 fl-s-e fl-a-c"
        >
          <div class="total-container text-left" style="width: 33%">
            {{ `${$t("field.vat")}:` }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ selectedBooking?.currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ totalTax }}
          </div>
        </div>

        <!-- Total -->
        <div
          v-if="(isInPaymentStep || isCompletedBooking) && !isEditOrder"
          class="w-full d-flex flex-1 fl-s-e fl-a-c"
        >
          <div class="total-container text-left" style="width: 33%">
            {{ `${$t("field.total")}:` }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ selectedBooking?.currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ total1 }}
          </div>
        </div>

        <!-- Total - multi currencies -->
        <div
          v-if="
            (isInPaymentStep || isCompletedBooking) &&
            !isEditOrder &&
            splitPayments?.length
          "
        >
          <div
            v-for="(currency, index) in currenciesUsedInPayment"
            :key="index"
          >
            <div
              v-if="currency && currency.label !== groupCurrency.label"
              class="w-full d-flex flex-1 fl-s-e fl-a-c"
            >
              <div class="total-container text-left" style="width: 33%">
                {{ `${$t("field.total")}:` }}
              </div>
              <div class="total-container text-center p-rel" style="width: 33%">
                {{ currency.label }}

                <span style="position: absolute; right: -1rem"
                  >R {{ currency.exchangeRate }}</span
                >
              </div>
              <div class="total-container text-right" style="width: 33%">
                {{ (Number(total1) * currency.exchangeRate).toFixed(2) }}
              </div>
            </div>
          </div>
        </div>

        <!-- SHOWING CASH AMOUNT (REMOVABLE) -->
        <!-- Total - Edit Order -->
        <div v-if="isEditOrder" class="w-full d-flex flex-1 fl-s-e fl-a-c">
          <div class="total-container text-left" style="width: 33%">
            {{ `${$t("field.total")}:` }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ selectedBooking?.currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ orderValueExcludingCancelledTickets }}
          </div>
        </div>

        <!-- Payment amounts (all payment accountings) -->
        <div
          v-if="splitPayments && splitPayments.length > 0"
          style="padding-top: 40px"
        >
          <div
            v-for="payment in splitPayments"
            :key="payment.method"
            class="w-full d-flex flex-1 fl-s-e fl-a-c amounts-paid"
            style="min-height: 25px"
          >
            <div>
              <p>{{ payment.translatedMethod }}</p>
              <span
                style="font-size: 11px; font-weight: 400"
                v-if="payment.isVoucherPaymentPending"
                >Pending Payment</span
              >
            </div>
            <div class="text-center" style="width: 33%">
              <p>{{ payment.currency?.label }}</p>
            </div>
            <div class="d-flex flex-row fl-s-b" style="width: 33%">
              <div class="d-flex fl-col fl-j-c">
                <!-- Remove button is not shown in case of Adyen or in case of an old payment coming from backend (acounting with Id)
                payment.accountingId is not enough to identify accounting coming from backend since we are now enabling user to edit
                and order that he has just completed
                -->
                <Trash2Icon
                  style="margin-right: 5px"
                  v-if="showRemovePaymentBtn(payment)"
                  class="remove-btn"
                  @click="removePayment(payment.accountingUuid)"
                />
              </div>
              <div>
                <p>
                  {{
                    payment.cashBill
                      ? payment.cashBill.toFixed(2)
                      : (payment.amount * (payment.exchangeRate || 1)).toFixed(
                          2
                        )
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Total Paid & Total Refunded -->
        <!-- SHOW WHAT WAS PAID WITH BOOKINGS ACCEPTING SPLIT PAYMENT (BASED ON CONFIRMED ACCOUNTINGS) -->
        <div
          style="border-top: 2px solid white"
          v-if="splitPayments && splitPayments.length > 0"
        >
          <div class="d-flex flex-1 fl-s-e fl-a-c" style="margin-bottom: -15px">
            <div class="confirmed-amount-total text-left" style="width: 33%">
              {{ $t("asideordersummary.amount-confirmed") }}
            </div>
            <div class="confirmed-amount-total text-center" style="width: 33%">
              {{ payingCurrency }}
            </div>
            <div class="confirmed-amount-total text-right" style="width: 33%">
              {{ (amountPaid / 100).toFixed(2) }}
            </div>
          </div>

          <div
            v-if="isEditOrder"
            class="d-flex flex-1 fl-s-e fl-a-c"
            style="margin-bottom: -15px"
          >
            <div class="confirmed-amount-total text-left" style="width: 33%">
              {{ $t("asideordersummary.amount-refunded") }}
            </div>
            <div class="confirmed-amount-total text-center" style="width: 33%">
              {{ payingCurrency }}
            </div>
            <div class="confirmed-amount-total text-right" style="width: 33%">
              {{ refundedAmount.toFixed(2) }}
            </div>
          </div>

          <!-- Belgian cash rounding, negated so it reads as an adjustment
               against the amount the customer handed over -->
          <div
            v-if="roundingTotal"
            class="d-flex flex-1 fl-s-e fl-a-c"
            style="margin-bottom: -15px"
          >
            <div class="confirmed-amount-total text-left" style="width: 33%">
              {{ $t("payment-method.CASH_ROUNDING") }}
            </div>
            <div class="confirmed-amount-total text-center" style="width: 33%">
              {{ payingCurrency }}
            </div>
            <div class="confirmed-amount-total text-right" style="width: 33%">
              {{ ((roundingTotal / 100) * -1).toFixed(2) }}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="accountings-container d-flex flex-row fl-s-b">
          <p class="title d-flex fl-col fl-j-c">
            {{ $t("asideordersummary.balance-in") }} {{ currency }}
          </p>
          <p
            class="value d-flex fl-col fl-j-c"
            v-if="
              !selectedBooking?.type ||
              selectedBooking?.type !== BookingType.Invoice
            "
            data-cy="balance-amount"
          >
            <!-- v-if="selectedBooking.reference !== 'dce435bd'" -->
            {{ whatIsLeftToPay }}
          </p>
          <p class="value d-flex fl-col fl-j-c" v-else>
            <!-- v-if="selectedBooking.reference !== 'dce435bd'" -->
            {{ whatIsLeftToPayForInvoice }}
          </p>
        </div>
      </div>
    </div>
    <SButton
      v-if="isInOrderStep || isInPaymentStep"
      type="button"
      variant="outline"
      size="lg"
      :class="[
        'w-full keyboard-button',
        { hidden: calcVisibility && isInOrderStep },
      ]"
      :disabled="!isInOrderStep"
      @click="calcVisibility = !calcVisibility"
    >
      <SvgIcon
        :iconContent="calcIcon"
        :iconAlt="calcAlt"
        :iconStyle="calcIconStyle"
      />
    </SButton>
    <div class="calculator-wrapper" v-if="calcVisibility && isInOrderStep">
      <numeric-calculator
        :visible="calcVisibility"
        @closeCalc="closeCalculator"
      ></numeric-calculator>
    </div>

    <div v-if="showCancelTicketsModal" class="cancel-tickets-popup">
      <SDialog
        :open="showCancelTicketsModal"
        @update:open="(open) => !open && closeModal()"
      >
        <SDialogContent
          class="cancel-tickets-dialog !w-[960px] !max-w-[960px] !max-h-[85vh] !p-0 overflow-hidden"
          :class="
            // The footer row is conditional:
            // a fixed 3-row grid would leave a trailing gap when it is hidden.
            selectedConfirmedPssCount > 0
              ? '!grid-rows-[auto_minmax(0,1fr)_auto]'
              : '!grid-rows-[auto_minmax(0,1fr)]'
          "
          @escape-key-down.prevent
          @pointer-down-outside.prevent
          @open-auto-focus.prevent
        >
          <SDialogHeader>
            <SDialogTitle>Tickets Cancellation</SDialogTitle>
          </SDialogHeader>
          <div class="confirmed-tickets-container min-h-0 overflow-y-auto">
            <el-row class="confirmed-tickets-header sticky top-0 z-10">
              <el-col :span="1" class="d-flex fl-col fl-j-c">
                <SCheckbox
                  :model-value="isAllConfirmedPssSelected"
                  @update:model-value="
                    (v) => toggleSelectAllConfirmedPss(Boolean(v))
                  "
                  :disabled="isRemoveButtonLoading"
                  data-cy="confirm-remove-pss-select-all"
                />
              </el-col>
              <el-col :span="confirmedPSSToDelete[0].seat ? 6 : 7">{{
                $t("common.ticket-name")
              }}</el-col>
              <el-col v-if="confirmedPSSToDelete[0].seat" :span="3">{{
                $t("field.seat")
              }}</el-col>
              <el-col :span="4">{{ $t("common.price-name") }}</el-col>
              <el-col :span="4">{{ $t("common.price-value") }}</el-col>
              <el-col :span="confirmedPSSToDelete[0].seat ? 5 : 7">{{
                $t("common.bar-code")
              }}</el-col>
              <el-col :span="1"></el-col>
            </el-row>
            <div
              v-for="(pss, index) in confirmedPSSToDelete"
              :key="pss.uuid || index"
            >
              <el-row
                class="confirmed-ticket-row"
                :class="{
                  'confirmed-ticket-row-selected': isConfirmedPssSelected(
                    String(pss.uuid)
                  ),
                }"
                :style="
                  index !== confirmedPSSToDelete.length - 1
                    ? 'border-bottom: 1px solid #d5d5d5'
                    : ''
                "
              >
                <el-col :span="1" class="d-flex fl-col fl-j-c">
                  <SCheckbox
                    :model-value="isConfirmedPssSelected(String(pss.uuid))"
                    @update:model-value="
                      () => toggleConfirmedPssSelection(String(pss.uuid))
                    "
                    :disabled="isRemoveButtonLoading"
                    data-cy="confirm-remove-pss-select-one"
                  />
                </el-col>
                <el-col
                  class="confirmed-ticket-name d-flex fl-col fl-j-c"
                  :span="confirmedPSSToDelete[0].seat ? 6 : 7"
                >
                  <p>{{ pss.categoryName }}</p>
                </el-col>
                <el-col
                  v-if="confirmedPSSToDelete[0].seat"
                  class="confirmed-ticket-name d-flex fl-col fl-j-c"
                  :span="3"
                >
                  <p>{{ pss.seat }}</p>
                </el-col>
                <el-col :span="4" class="d-flex fl-col fl-j-c">
                  <p>{{ pss.priceName }}</p>
                </el-col>
                <el-col :span="4" class="d-flex fl-col fl-j-c">
                  <p class="confirmed-ticket-price">
                    {{
                      currency +
                      " " +
                      (getPackageTotalValue(pss) / 100).toFixed(2)
                    }}
                  </p>
                </el-col>
                <el-col
                  :span="confirmedPSSToDelete[0].seat ? 5 : 7"
                  class="d-flex fl-col fl-j-c"
                >
                  <p>{{ pss.barCode ?? "-" }}</p>
                </el-col>
                <el-col
                  :span="1"
                  class="confirmed-ticket-delete d-flex fl-col fl-j-c"
                >
                  <div>
                    <SButton
                      v-if="pss.bookingStatus === BookingStatus.Confirmed"
                      type="button"
                      variant="ghost"
                      size="icon"
                      class="confirmed-ticket-delete-btn text-destructive"
                      :loading="isRemoveButtonLoading"
                      :disabled="isRemoveButtonLoading"
                      aria-label="Cancel ticket"
                      data-cy="confirm-remove-pss-button"
                      @click="openMessageBox(pss)"
                    >
                      <Trash2Icon />
                    </SButton>
                  </div>
                </el-col>
              </el-row>
            </div>
          </div>
          <SDialogFooter v-if="selectedConfirmedPssCount > 0">
            <div class="confirmed-tickets-bulk-footer">
              <p class="confirmed-tickets-bulk-count">
                {{ selectedConfirmedPssCount }} ticket(s) selected
              </p>
              <SButton
                type="button"
                class="confirmed-tickets-bulk-btn"
                :loading="isRemoveButtonLoading"
                :disabled="isRemoveButtonLoading"
                data-cy="bulk-remove-pss-button"
                @click="bulkCancelSelectedTickets"
              >
                Cancel selected tickets
              </SButton>
            </div>
          </SDialogFooter>
        </SDialogContent>
      </SDialog>
    </div>

    <div v-if="showCancelConfirmationModal" class="cancel-confirmation-popup">
      <SDialog
        :open="showCancelConfirmationModal"
        @update:open="(open) => !open && closeCancelConfirmationModal()"
      >
        <SDialogContent
          :show-close-button="false"
          class="cancel-confirmation-dialog !w-[450px] !max-w-[450px] !p-0"
          data-cy="cancel-tickets-confirmation-modal"
          @escape-key-down.prevent
          @pointer-down-outside.prevent
        >
          <SDialogHeader>
            <SDialogTitle class="sr-only">Cancel ticket(s)?</SDialogTitle>
          </SDialogHeader>
          <div class="cancel-confirmation-content">
            <div class="cancel-confirmation-icon-wrapper">
              <SvgIcon
                :iconContent="alertTriangleIcon"
                :iconAlt="'alert'"
                class="cancel-confirmation-icon"
              />
            </div>
            <h3 class="cancel-confirmation-title">Cancel ticket(s)?</h3>
            <p class="cancel-confirmation-text">
              Are you sure you want to cancel {{ cancelConfirmationCount }}
              ticket(s)? This action cannot be turned back.
            </p>
            <div class="cancel-confirmation-actions">
              <SButton
                type="button"
                variant="outline"
                class="cancel-confirmation-btn-secondary"
                data-cy="cancel-tickets-confirmation-cancel-button"
                @click="closeCancelConfirmationModal"
              >
                Cancel
              </SButton>
              <SButton
                type="button"
                class="cancel-confirmation-btn-primary"
                :loading="isRemoveButtonLoading"
                :disabled="isRemoveButtonLoading"
                data-cy="cancel-tickets-confirmation-ok-button"
                @click="confirmTicketsCancellation"
              >
                Ok
              </SButton>
            </div>
          </div>
        </SDialogContent>
      </SDialog>
    </div>

    <!-- Waiver modals -->
    <LinkWaiverModal />
    <ShareWaiverModal />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import NumericCalculator from "./NumericCalculator.vue";
import WaiverSection from "@/components/layout/aside-order-summary/WaiverSection.vue";
import LinkWaiverModal from "@/components/modal/waivers/LinkWaiverModal.vue";
import ShareWaiverModal from "@/components/modal/waivers/ShareWaiverModal.vue";
import { setupAsideOrderSummary } from "./aside-order-summary";
import {
  SButton,
  SCheckbox,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import { Trash2Icon } from "@smeetz/ui-icons";
import {
  hasCustomerDisplay,
  updateCustomerScreen,
} from "@/utils/vue-helpers/customer-display";
import copyIcon from "@/assets/images/svg/copy.svg";
import check from "@/assets/images/svg/green-check.svg";
import alertTriangleIcon from "@/assets/images/svg/alert-triangle.svg";
import { BookingType } from "@/types/db/product-slot-booking";
import { provide } from "vue";
import { WAIVER_COUNT_KEY } from "@/components/layout/aside-order-summary/waiver-keys";

export default defineComponent({
  components: {
    NumericCalculator,
    WaiverSection,
    LinkWaiverModal,
    ShareWaiverModal,
    SButton,
    SCheckbox,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    Trash2Icon,
  },
  name: "AsideOrderSummary",
  setup() {
    const {
      getStatusTranslation,
      showRemoveBtn,
      showRemovePaymentBtn,
      isInThePast,
      showCancelTicketsModal,
      iconContent,
      iconAlt,
      iconStyle,
      printIcon,
      printAlt,
      t,
      bookings,
      bookingList,
      selectedBooking,
      currency,
      pssList,
      isCompletedBooking,
      isWaiverAllowed,
      bookingStep,
      BookingStep,
      isInPaymentStep,
      isInOrderStep,
      confirmedAmountsInfo,
      confirmedAmountsTotal,
      payingCurrency,
      getCustomerFullName,
      hasAppliedDiscount,
      isDiscountOnAllOrder,
      discountTotal1,
      discountTotal,
      whatIsLeftToPay,
      whatIsLeftToPayForInvoice,
      total1,
      total,
      isLongTitle,
      isPSSConsideredInDiscount1,
      isPSSConsideredInDiscount,
      isFreeTicket,
      PSSType,
      onClickCustomers,
      getPSSNewPriceValue,
      getTicketDiscount1,
      groupedPSSList,
      deleteUnconfirmedTickets,
      AccountingState,
      deleteTickets,
      showTicketInfo,
      formatDateTime,
      removePayment,
      deleteConfirmedAmount,
      isAccountingCompleted,
      doesSupportSplitPayment,
      balance1,
      isEditOrder,
      alreadyPaidAmount,
      refundedAmount,
      roundingTotal,
      refreshDiscount,
      calcVisibility,
      calcIcon,
      calcIconStyle,
      calcAlt,
      closeCalculator,
      BookingStatus,
      confirmedPSSToDelete,
      openMessageBox,
      closeModal,
      selectedConfirmedPssCount,
      isAllConfirmedPssSelected,
      showCancelConfirmationModal,
      cancelConfirmationCount,
      isConfirmedPssSelected,
      toggleConfirmedPssSelection,
      toggleSelectAllConfirmedPss,
      bulkCancelSelectedTickets,
      closeCancelConfirmationModal,
      confirmTicketsCancellation,
      orderValueExcludingCancelledTickets,
      splitPayments,
      amountPaid,
      translatePaymentMethods,
      totalTax,
      PaymentMethods,
      bookingSynchingDateTime,
      hasGenericDiscountAccountings,
      groupCurrency,
      currenciesUsedInPayment,
      getPSSValue,
      getPackageTotalValue,
      isRemoveButtonLoading,
      getTicketAppliedDiscountCodes,
      orderReference,
      copyToClipboard,
      showCopyCheck,
      getPssGroupDiscount,
      getTicketAppliedOffers,
      printBill,
      showPrintBill,
      linkedCount,
      isLoadingCount,
      incrementLinkedCount,
      decrementLinkedCount,
    } = setupAsideOrderSummary();

    provide(WAIVER_COUNT_KEY, {
      incrementCount: incrementLinkedCount,
      decrementCount: decrementLinkedCount,
    });

    return {
      getStatusTranslation,
      showRemoveBtn,
      showRemovePaymentBtn,
      isInThePast,
      showCancelTicketsModal,
      iconContent,
      iconAlt,
      iconStyle,
      printIcon,
      printAlt,
      t,
      bookings,
      bookingList,
      selectedBooking,
      currency,
      pssList,
      isCompletedBooking,
      isWaiverAllowed,
      bookingStep,
      BookingStep,
      isInPaymentStep,
      isInOrderStep,
      confirmedAmountsInfo,
      confirmedAmountsTotal,
      payingCurrency,
      getCustomerFullName,
      hasAppliedDiscount,
      isDiscountOnAllOrder,
      discountTotal1,
      discountTotal,
      whatIsLeftToPay,
      whatIsLeftToPayForInvoice,
      BookingType,
      total1,
      total,
      isLongTitle,
      isPSSConsideredInDiscount1,
      isPSSConsideredInDiscount,
      isFreeTicket,
      PSSType,
      onClickCustomers,
      getPSSNewPriceValue,
      getTicketDiscount1,
      groupedPSSList,
      deleteUnconfirmedTickets,
      AccountingState,
      deleteTickets,
      showTicketInfo,
      formatDateTime,
      removePayment,
      deleteConfirmedAmount,
      isAccountingCompleted,
      doesSupportSplitPayment,
      balance1,
      isEditOrder,
      alreadyPaidAmount,
      refundedAmount,
      roundingTotal,
      refreshDiscount,
      calcVisibility,
      calcIcon,
      calcIconStyle,
      calcAlt,
      closeCalculator,
      // paidAccountingsSum,
      BookingStatus,
      confirmedPSSToDelete,
      openMessageBox,
      closeModal,
      selectedConfirmedPssCount,
      isAllConfirmedPssSelected,
      showCancelConfirmationModal,
      cancelConfirmationCount,
      isConfirmedPssSelected,
      toggleConfirmedPssSelection,
      toggleSelectAllConfirmedPss,
      bulkCancelSelectedTickets,
      closeCancelConfirmationModal,
      confirmTicketsCancellation,
      orderValueExcludingCancelledTickets,
      splitPayments,
      amountPaid,
      translatePaymentMethods,
      totalTax,
      PaymentMethods,
      bookingSynchingDateTime,
      hasGenericDiscountAccountings,
      groupCurrency,
      currenciesUsedInPayment,
      getPSSValue,
      getPackageTotalValue,
      isRemoveButtonLoading,
      getTicketAppliedDiscountCodes,
      copyIcon,
      alertTriangleIcon,
      orderReference,
      copyToClipboard,
      check,
      showCopyCheck,
      getPssGroupDiscount,
      getTicketAppliedOffers,
      printBill,
      showPrintBill,
      linkedCount,
      isLoadingCount,
    };
  },
  created() {
    // console.log("hi from created of order summary()");
    // console.log("********* nowDB()");
    // console.log(nowDB());
  },
  mounted() {
    // console.log("hi from mounted of order summary()");
    // console.log("********* nowDB()");
    // console.log(nowDB());
    // this.specialWhatIsLeftToPay = (BookingModule.leftToPayAmount / 100).toFixed(
    //   2
    // );
  },
  async updated() {
    console.log("hi from updated of AsideOrderSummary");
    if (hasCustomerDisplay.value) {
      await updateCustomerScreen();
    }
  },
});
</script>

<style lang="scss">
@import "@/styles/variables.scss";

$summary-width: 390px;

.el-aside {
  width: unset;
}

.order-summary-wrapper {
  flex-grow: 3;
  width: $summary-width;
  // padding: 5px 15px;
  padding-top: 5px;
  padding-bottom: 5px;
  border-radius: $border-radius;
  background-color: #313335;
  color: white;

  .remove-btn {
    --s-icon-size: 17px;
    color: $color-red;
    cursor: pointer;
  }

  .order-summary {
    padding-left: 15px;
    padding-right: 15px;
    .upper-part-container {
      height: 62px;
      min-height: 62px;

      .title {
        text-align: left;
        letter-spacing: 0px;
        color: white;
        font-weight: 700;
        font-style: normal;
        font-size: 22px;
        padding-top: 15px;
      }

      .max-content-width {
        width: max-content;
      }

      .long-title {
        font-size: 16px;
        line-height: 20px;
      }

      .assign-customer-btn-container,
      .print-receipt-btn-container {
        cursor: pointer;
        line-height: 15px;
        letter-spacing: 0px;
        color: white;
        font-weight: 400;
        font-style: normal;
        font-size: 11px;
        text-align: center;
        padding-left: 10px;

        .icon-container {
          margin-right: 5px;
        }
      }
    }

    .pss-list-container {
      padding-top: 10px;
      padding-bottom: 10px;

      .pss-container {
        // height: fit-content;
        padding-top: 5px;
        padding-bottom: 5px;
        min-height: 80px;
        align-items: center;
        border-bottom: 1px solid white;

        .ticket-name-container {
          width: 50%;
          text-align: left;

          .ticket-name {
            font-weight: 700;
            font-style: normal;
            font-size: 14px;
            line-height: 14px;
            margin-bottom: 5px;
          }
          .price-name,
          .ticket-discount-info {
            font-weight: 400;
            font-style: normal;
            font-size: 10px;
            line-height: 14px;
            color: #959595;
            font-weight: 400;
          }
          .timeslot {
            font-weight: 400;
            font-style: normal;
            font-size: 10px;
            line-height: 14px;
          }
          .seat {
            font-weight: 400;
            font-style: normal;
            font-size: 10px;
            line-height: 14px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        }

        .quantity-container {
          width: 10%;
          text-align: center;
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;

          .quantity {
            font-weight: 700;
            font-style: normal;
            font-size: 12px;
            text-align: right;
            line-height: 14px;
            letter-spacing: 0px;
            margin-right: 8px;
          }
        }

        .unit-price-container {
          width: 20%;
          text-align: center;

          .unit-price {
            font-weight: 700;
            font-style: normal;
            font-size: 12px;
            text-align: right;
            line-height: 14px;
            letter-spacing: 0px;
          }
        }
        .total-price-container.with-pt {
          padding-top: 1.2rem;
        }
        .total-price-container {
          width: 20%;
          text-align: right;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          .total-price {
            font-weight: 700;
            font-style: normal;
            font-size: 12px;
            text-align: right;
            line-height: 14px;
            letter-spacing: 0px;
          }
        }

        .erased-tickets {
          text-decoration: line-through;
        }
      }

      .tickets-status-container {
        font-weight: 700;
        font-style: normal;
        font-size: 12px;
        line-height: 12px;
        text-align: right;
        width: 100%;
        color: yellow;
        margin-top: -15px;
        padding-bottom: 5px;
      }

      .cancelled-pss-container {
        background-color: rgba(
          $color: $color-grid-button-purple /*$color-dark-grey-2 */,
          $alpha: 0.5
        );
      }
    }

    .total-container {
      line-height: 30px;
      height: 30px;
      font-weight: 700;
      font-style: normal;
      font-size: 14px;
      // padding-left: 1%;
    }

    .tax-container {
      border-bottom: 1px solid white;
    }

    .discount-container {
      font-size: 12px;
      line-height: 30px;
      height: 30px;

      img {
        height: 17px;
        width: 17px;
        cursor: pointer;
      }
    }

    .confirmed-amount-container {
      border-bottom: 1px solid white;
      border-top: 1px solid white;

      .confirmed-amount {
        height: 40px;
        font-weight: 700;
        font-style: normal;
        font-size: 14px;
        line-height: 14px;
        margin-bottom: 5px;

        img {
          height: 17px;
          width: 17px;
          cursor: pointer;
        }
      }
    }

    .confirmed-amount-total {
      font-weight: 700;
      font-style: normal;
      font-size: 14px;
      // text-align: right;
      line-height: 50px;
      // height: 50px;
      letter-spacing: 0px;
    }
  }

  .accountings-container {
    height: 45px;
    border-top: 2px solid white;
    padding-left: 15px;
    padding-right: 15px;

    .title {
      text-align: left;
    }
    .value {
      text-align: right;
    }
    .title,
    .value {
      font-weight: 700;
      font-style: normal;
      font-size: 16px;
      text-align: left;
      line-height: 20px;
      letter-spacing: 0px;
    }
  }
}

.discount-hundreds {
  padding-left: 2%;
}

.discount-dozens {
  padding-left: 4%;
}

.confirmed-ones {
  padding-left: 3%;
}

.confirmed-dozens {
  padding-left: 5%;
}

.confirmed-hundreds {
  padding-left: 1%;
}

.keyboard-button {
  margin-top: 13px;
  padding: 24px;
  width: 390px;
  height: 45px;
  background: rgba(75, 155, 165, 0.05);
  border: 1px solid rgba(51, 51, 51, 0.25);
  border-radius: 4px;
}

.calculator-wrapper {
  margin-top: 6px;
  border: 1px solid rgba(51, 51, 51, 0.15);
  border-radius: 5px;
}

.aside-wrapper {
  width: 390px;
  height: 72vh;
  min-height: 100%;
}

.cancel-tickets-dialog {
  border-radius: 6px;

  [data-slot="dialog-header"] {
    padding: 14px 16px 8px;
    border-bottom: 0;
  }

  [data-slot="dialog-title"] {
    font-size: 20px;
    line-height: 26px;
    font-weight: 700;
    color: #141a23;
  }

  [data-slot="dialog-close"] {
    color: #586473;
    font-size: 16px;
    font-weight: 400;
  }

  [data-slot="dialog-footer"] {
    border-top: 1px solid #d5d5d5;
    padding: 6px 12px;
    margin-top: 0;
  }

  .confirmed-tickets-container {
    padding: 0 16px;
    border-bottom: 1px solid #d5d5d5;
    scrollbar-width: thin;
    scrollbar-color: #8f8f8f transparent;
  }

  .confirmed-tickets-container::-webkit-scrollbar {
    width: 6px;
  }

  .confirmed-tickets-container::-webkit-scrollbar-track {
    background: transparent;
    margin: 0;
    border: 0;
  }

  .confirmed-tickets-container::-webkit-scrollbar-thumb {
    background: #8f8f8f;
    border-radius: 8px;
  }

  .confirmed-tickets-header {
    font-size: 12px;
    font-weight: 700;
    border-bottom: 1px solid #d5d5d5;
    padding: 6px 12px 8px;
    color: #617085;
    background: #fff;
    align-items: center;
  }

  .confirmed-ticket-row {
    padding: 0 12px;
    transition: background-color 0.15s ease;
    min-height: 58px;
    color: #141a23;
    font-size: 11px;
    align-items: center;
  }

  .confirmed-ticket-row-selected {
    background: #ebeff4;
  }

  .confirmed-ticket-name {
    font-weight: 700;
  }

  .confirmed-ticket-price {
    font-weight: 700;
  }

  .confirmed-ticket-row .el-col {
    min-height: 58px;
    line-height: 16px;
  }

  .confirmed-ticket-row p {
    margin: 0;
  }

  .confirmed-ticket-delete {
    text-align: right;
    padding-right: 2px;
  }

  .confirmed-ticket-delete-btn {
    color: #ff5b59;
    border: 0;
    padding: 0;
    width: 18px;
    height: 18px;
    min-height: 18px;
    border-radius: 0;
    background: transparent;
  }

  .confirmed-ticket-delete-btn:hover,
  .confirmed-ticket-delete-btn:focus {
    color: #ff5b59;
    background: transparent;
  }

  .confirmed-tickets-bulk-footer {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 46px;
    padding: 0;
  }

  .confirmed-tickets-bulk-count {
    font-size: 12px;
    line-height: 16px;
    color: #141a23;
    margin: 0;
    font-weight: 500;
  }

  .confirmed-tickets-bulk-btn {
    background: #0f9b8e !important;
    border: 1px solid #0f9b8e !important;
    color: white !important;
    border-radius: 8px;
    min-height: 34px;
    padding: 0 16px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
  }

  .confirmed-tickets-bulk-btn:hover,
  .confirmed-tickets-bulk-btn:focus {
    background: #0f9b8e !important;
    border-color: #0f9b8e !important;
    color: white !important;
  }

  .confirmed-tickets-bulk-btn.is-disabled,
  .confirmed-tickets-bulk-btn.is-disabled:hover {
    background: #0f9b8e !important;
    border-color: #0f9b8e !important;
    color: white !important;
    opacity: 0.45;
  }

  .el-checkbox__inner {
    border-color: #0f9b8e;
    width: 14px;
    height: 14px;
  }

  .el-checkbox {
    display: inline-flex;
    align-items: center;
  }

  .el-checkbox__input.is-checked .el-checkbox__inner,
  .el-checkbox__input.is-indeterminate .el-checkbox__inner {
    background-color: #0f9b8e;
    border-color: #0f9b8e;
  }
}

.cancel-confirmation-dialog {
  border-radius: 10px;
  max-width: 430px;

  [data-slot="dialog-header"] {
    display: none;
  }

  .cancel-confirmation-content {
    padding: 20px 22px 16px;
    text-align: center;
  }

  .cancel-confirmation-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fdeecf;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }

  .cancel-confirmation-icon {
    width: 18px;
    height: 18px;
  }

  .cancel-confirmation-title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 700;
    color: #141a23;
    margin: 0 0 8px;
  }

  .cancel-confirmation-text {
    font-size: 13px;
    line-height: 20px;
    color: #65758c;
    margin: 0;
  }

  .cancel-confirmation-actions {
    margin-top: 14px;
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  .cancel-confirmation-btn-secondary,
  .cancel-confirmation-btn-primary {
    min-height: 36px;
    padding: 0 18px;
    border-radius: 7px;
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
  }

  .cancel-confirmation-btn-secondary {
    border: 1px solid #d8dee8;
    color: #141a23;
    background: #fff;
  }

  .cancel-confirmation-btn-primary {
    background: #10998d !important;
    border: 1px solid #10998d !important;
    color: #fff !important;
  }
}

.amounts-paid {
  font-weight: 800;
  font-style: normal;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
}

.order-reference {
  font-weight: 400;
  font-style: normal;
  font-size: 0.8rem;
  line-height: 14px;
  color: #959595;
  font-weight: 400;
}
.order-reference-wrapper {
  display: flex;
  align-items: center;
  gap: 5px;
}
.check {
  padding-bottom: 5px;
}
.clipboard {
  padding-bottom: 5px;
}
</style>
